import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BrandService, BrandSupplierRelationship } from "@/lib/brand-service";
import { BrandVerificationBadge } from "./brand-verification-badge";
import { PlusCircle, Trash2, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface SupplierBrandManagementProps {
  supplierId: string;
}

export function SupplierBrandManagement({ supplierId }: SupplierBrandManagementProps) {
  const [relationships, setRelationships] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const { toast } = useToast();
  const brandService = new BrandService();
  const navigate = useNavigate();
  
  useEffect(() => {
    loadRelationships();
  }, [supplierId]);
  
  const loadRelationships = async () => {
    if (!supplierId) return;
    
    setLoading(true);
    try {
      const data = await brandService.getSupplierBrandRelationships(supplierId);
      setRelationships(data);
    } catch (error) {
      console.error('Error loading brand relationships:', error);
      toast({
        title: "Error",
        description: "Failed to load brand relationships",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteRelationship = async (relationshipId: string) => {
    if (!relationshipId) return;
    
    try {
      const success = await brandService.deleteBrandSupplierRelationship(relationshipId);
      
      if (success) {
        toast({
          title: "Success",
          description: "Brand relationship removed successfully",
        });
        await loadRelationships();
      } else {
        throw new Error("Failed to delete relationship");
      }
    } catch (error) {
      console.error('Error deleting brand relationship:', error);
      toast({
        title: "Error",
        description: "Failed to remove brand relationship",
        variant: "destructive"
      });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Brand Relationships</CardTitle>
        <Button 
          size="sm"
          onClick={() => navigate('/add-brand')}
        >
          <PlusCircle className="h-4 w-4 mr-2" />
          Add Brand
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-4">
            <Loader2 className="h-6 w-6 animate-spin mx-auto" />
            <p className="mt-2 text-sm text-muted-foreground">Loading brand relationships...</p>
          </div>
        ) : relationships.length > 0 ? (
          <div className="space-y-4">
            {relationships.map(relationship => (
              <div key={relationship.id} className="flex items-center justify-between border-b pb-3">
                <div className="flex items-center gap-3">
                  {relationship.brands?.logo_url && (
                    <img 
                      src={relationship.brands.logo_url} 
                      alt={relationship.brands.name}
                      className="h-10 w-10 object-contain"
                    />
                  )}
                  <div>
                    <div className="font-medium">{relationship.brands?.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {relationship.territory ? `Territory: ${relationship.territory}` : 'No territory specified'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <BrandVerificationBadge 
                    verified={relationship.is_verified}
                    relationshipType={relationship.relationship_type}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteRelationship(relationship.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-muted-foreground">No brand relationships added yet.</p>
            <Button 
              variant="outline" 
              className="mt-2"
              onClick={() => navigate('/add-brand')}
            >
              Add Your First Brand Relationship
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
